/**
 * Utility functions for fetching and processing blog posts
 */

/**
 * Parses frontmatter metadata from markdown content
 * @param {string} content - Raw markdown content
 * @returns {Object} - { metadata, content } where metadata is parsed from frontmatter
 */
export function parseMarkdownWithFrontmatter(content) {
  // If content doesn't start with frontmatter delimiter, return empty metadata
  if (!content.startsWith('---')) {
    return {
      metadata: {},
      content
    };
  }

  // Find the end of the frontmatter section
  const endOfFrontmatter = content.indexOf('---', 3);
  if (endOfFrontmatter === -1) {
    return {
      metadata: {},
      content
    };
  }

  // Extract the frontmatter section
  const frontmatter = content.substring(3, endOfFrontmatter).trim();
  const mainContent = content.substring(endOfFrontmatter + 3).trim();
  
  // Parse frontmatter into metadata object
  const metadata = {};
  frontmatter.split('\n').forEach(line => {
    const [key, ...valueParts] = line.split(':');
    
    if (key && valueParts.length) {
      const value = valueParts.join(':').trim();
      
      // Handle arrays in tags format [tag1, tag2, tag3]
      if (key.trim() === 'tags' && value.startsWith('[') && value.endsWith(']')) {
        metadata.tags = value
          .slice(1, -1)  // Remove brackets
          .split(',')    // Split by comma
          .map(tag => tag.trim()); // Trim each tag
      } else {
        metadata[key.trim()] = value;
      }
    }
  });

  // Return metadata and content separately
  return {
    metadata,
    content: mainContent
  };
}

/**
 * Fetches blog posts from JSON files
 * @returns {Promise<Array>} Array of blog post objects
 */
export const fetchBlogPosts = async () => {
  try {
    // 1. Fetch the JSON manifest
    const manifestResponse = await fetch('/blogs-json/manifest.json', {
      cache: 'no-store'
    });
    
    if (!manifestResponse.ok) {
      throw new Error(`Failed to load blog manifest: ${manifestResponse.status}`);
    }
    
    const manifest = await manifestResponse.json();
    
    if (!manifest.posts || !manifest.posts.length) {
      return [];
    }
    
    // 2. Fetch each blog post JSON file
    const postPromises = manifest.posts.map(async (filename) => {
      try {
        const postResponse = await fetch(`/blogs-json/${filename}`, {
          headers: { 'Accept': 'application/json' },
          cache: 'no-store'
        });
        
        if (!postResponse.ok) {
          return null;
        }
        
        // Parse JSON data directly
        return await postResponse.json();
      } catch (error) {
        return null;
      }
    });
    
    // 3. Process all posts and sort by date
    const posts = (await Promise.all(postPromises))
      .filter(post => post !== null)
      .sort((a, b) => {
        try {
          return new Date(b.date) - new Date(a.date);
        } catch (err) {
          return 0;
        }
      });
    
    return posts;
  } catch (error) {
    throw error;
  }
};

/**
 * Helper function to process post data from filename and content
 * @param {string} filename - Filename of the post
 * @param {Object} metadata - Extracted metadata from frontmatter
 * @param {string} content - Post content without frontmatter
 * @returns {Object} Processed post object
 */
function processPostData(filename, metadata, content) {
  // Process filename with improved parsing
  const baseName = filename.replace(/\.md$/i, "");
  const [dateStr, ...titleParts] = baseName.split("_");
  
  // Enhanced title formatting
  const titleFromFilename = titleParts.join(" ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase()); // Capitalize first letter of each word
  
  // Parse date from filename
  let formattedDate;
  try {
    const month = dateStr.substring(0, 2);
    const day = dateStr.substring(2, 4);
    const year = dateStr.substring(4);
    formattedDate = `${year}-${month}-${day}`;
  } catch (err) {
    formattedDate = new Date().toISOString().split('T')[0];
  }
  
  return {
    id: baseName,
    title: metadata.title || titleFromFilename,
    date: metadata.date || formattedDate,
    description: metadata.description || '',
    tags: metadata.tags || [],
    content
  };
}
